import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SearchMessageHit, SearchMessagesResponse, searchMessages } from "./searchApi";

const DEFAULT_SEARCH_LIMIT = 20;
const DEFAULT_SEARCH_DEBOUNCE_MS = 280;

export interface UseMessageSearchOptions {
  initialQuery?: string;
  initialChatID?: string | null;
  limit?: number;
  debounceMs?: number;
  enabled?: boolean;
  searchFn?: (input: {
    query: string;
    chatID?: string | null;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }) => Promise<SearchMessagesResponse>;
}

export interface NormalizedSearchPage {
  hits: SearchMessageHit[];
  estimatedTotalHits: number;
  limit: number;
  offset: number;
  canLoadMore: boolean;
}

export interface UseMessageSearchResult {
  query: string;
  setQuery: (value: string) => void;
  chatID: string | null;
  setChatID: (value: string | null) => void;
  debouncedQuery: string;
  hits: SearchMessageHit[];
  totalHits: number;
  offset: number;
  limit: number;
  loading: boolean;
  loadingMore: boolean;
  hasSearched: boolean;
  empty: boolean;
  canLoadMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
  reset: () => void;
}

interface ExecuteSearchInput {
  query: string;
  chatID?: string;
  offset: number;
  append: boolean;
}

export function normalizeSearchQuery(value: string): string {
  return value.trim();
}

export function normalizeSearchChatID(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const normalized = value.trim();
  return normalized === "" ? undefined : normalized;
}

export function normalizeSearchLimit(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return DEFAULT_SEARCH_LIMIT;
  }
  const normalized = Math.trunc(value);
  if (normalized <= 0) {
    return DEFAULT_SEARCH_LIMIT;
  }
  return normalized;
}

function normalizeDebounceMs(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return DEFAULT_SEARCH_DEBOUNCE_MS;
  }
  const normalized = Math.trunc(value);
  if (normalized < 0) {
    return DEFAULT_SEARCH_DEBOUNCE_MS;
  }
  return normalized;
}

export function mergeSearchHits(existing: SearchMessageHit[], incoming: SearchMessageHit[]): SearchMessageHit[] {
  const byKey = new Map<string, SearchMessageHit>();
  for (const hit of existing) {
    byKey.set(`${hit.chat_id}:${hit.message_id}`, hit);
  }
  for (const hit of incoming) {
    byKey.set(`${hit.chat_id}:${hit.message_id}`, hit);
  }

  return [...byKey.values()].sort((left, right) => {
    if (left.chat_id !== right.chat_id) {
      return left.chat_id.localeCompare(right.chat_id);
    }
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
    return left.message_id.localeCompare(right.message_id);
  });
}

export function normalizeSearchResponse(
  response: SearchMessagesResponse,
  existingHits: SearchMessageHit[],
): NormalizedSearchPage {
  const incoming = Array.isArray(response.hits) ? response.hits : [];
  const merged = mergeSearchHits(existingHits, incoming);

  const normalizedLimit =
    Number.isFinite(response.limit) && response.limit > 0 ? Math.trunc(response.limit) : DEFAULT_SEARCH_LIMIT;
  const normalizedOffset =
    Number.isFinite(response.offset) && response.offset >= 0 ? Math.trunc(response.offset) : existingHits.length;

  const estimated = Number.isFinite(response.estimated_total_hits)
    ? Math.max(Math.trunc(response.estimated_total_hits), merged.length)
    : merged.length;

  return {
    hits: merged,
    estimatedTotalHits: estimated,
    limit: normalizedLimit,
    offset: normalizedOffset,
    canLoadMore: merged.length < estimated && incoming.length > 0,
  };
}

export function normalizeSearchError(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.trim();
    if (message !== "") {
      return message;
    }
  }
  return "Search request failed.";
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (delayMs === 0) {
      setDebounced(value);
      return;
    }

    const timeoutID = globalThis.setTimeout(() => {
      setDebounced(value);
    }, delayMs);

    return () => {
      globalThis.clearTimeout(timeoutID);
    };
  }, [delayMs, value]);

  return debounced;
}

export function useMessageSearch(options: UseMessageSearchOptions = {}): UseMessageSearchResult {
  const searchFn = options.searchFn ?? searchMessages;
  const enabled = options.enabled ?? true;
  const limit = normalizeSearchLimit(options.limit);
  const debounceMs = normalizeDebounceMs(options.debounceMs);

  const [query, setQuery] = useState(options.initialQuery ?? "");
  const [chatID, setChatID] = useState<string | null>(options.initialChatID ?? null);
  const [hits, setHits] = useState<SearchMessageHit[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [offset, setOffset] = useState(0);
  const [serverLimit, setServerLimit] = useState(limit);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeRequestRef = useRef(0);
  const hitsRef = useRef<SearchMessageHit[]>([]);

  useEffect(() => {
    hitsRef.current = hits;
  }, [hits]);

  const normalizedChatID = useMemo(() => normalizeSearchChatID(chatID), [chatID]);
  const debouncedQuery = useDebouncedValue(normalizeSearchQuery(query), debounceMs);

  const executeSearch = useCallback(
    async (input: ExecuteSearchInput): Promise<void> => {
      const requestID = activeRequestRef.current + 1;
      activeRequestRef.current = requestID;

      setHasSearched(true);
      setError(null);

      if (input.append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setHits([]);
        setTotalHits(0);
        setOffset(0);
        setServerLimit(limit);
      }

      try {
        const response = await searchFn({
          query: input.query,
          chatID: input.chatID,
          limit,
          offset: input.offset,
        });

        if (activeRequestRef.current !== requestID) {
          return;
        }

        const normalized = normalizeSearchResponse(response, input.append ? hitsRef.current : []);
        setHits(normalized.hits);
        setTotalHits(normalized.estimatedTotalHits);
        setOffset(normalized.offset);
        setServerLimit(normalized.limit);
      } catch (searchError) {
        if (activeRequestRef.current !== requestID) {
          return;
        }
        if (!input.append) {
          setHits([]);
          setTotalHits(0);
          setOffset(0);
        }
        setError(normalizeSearchError(searchError));
      } finally {
        if (activeRequestRef.current !== requestID) {
          return;
        }
        if (input.append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [limit, searchFn],
  );

  useEffect(() => {
    if (!enabled) {
      activeRequestRef.current += 1;
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      return;
    }

    if (debouncedQuery === "") {
      activeRequestRef.current += 1;
      setHits([]);
      setTotalHits(0);
      setOffset(0);
      setServerLimit(limit);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      setHasSearched(false);
      return;
    }

    void executeSearch({
      query: debouncedQuery,
      chatID: normalizedChatID,
      offset: 0,
      append: false,
    });
  }, [debouncedQuery, enabled, executeSearch, limit, normalizedChatID]);

  const canLoadMore = !loading && !loadingMore && error === null && hits.length > 0 && hits.length < totalHits;
  const empty = hasSearched && !loading && !loadingMore && error === null && hits.length === 0;

  const loadMore = useCallback(async () => {
    if (!enabled || debouncedQuery === "" || !canLoadMore) {
      return;
    }

    await executeSearch({
      query: debouncedQuery,
      chatID: normalizedChatID,
      offset: hitsRef.current.length,
      append: true,
    });
  }, [canLoadMore, debouncedQuery, enabled, executeSearch, normalizedChatID]);

  const reload = useCallback(async () => {
    if (!enabled) {
      return;
    }

    if (debouncedQuery === "") {
      activeRequestRef.current += 1;
      setHits([]);
      setTotalHits(0);
      setOffset(0);
      setServerLimit(limit);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      setHasSearched(false);
      return;
    }

    await executeSearch({
      query: debouncedQuery,
      chatID: normalizedChatID,
      offset: 0,
      append: false,
    });
  }, [debouncedQuery, enabled, executeSearch, limit, normalizedChatID]);

  const reset = useCallback(() => {
    activeRequestRef.current += 1;
    setQuery("");
    setHits([]);
    setTotalHits(0);
    setOffset(0);
    setServerLimit(limit);
    setLoading(false);
    setLoadingMore(false);
    setHasSearched(false);
    setError(null);
  }, [limit]);

  return {
    query,
    setQuery,
    chatID,
    setChatID,
    debouncedQuery,
    hits,
    totalHits,
    offset,
    limit: serverLimit,
    loading,
    loadingMore,
    hasSearched,
    empty,
    canLoadMore,
    error,
    loadMore,
    reload,
    reset,
  };
}
